import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ourclientsayimage from "../assets/OurClientSay.png";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locals/translations.json";
import api from "../lib/api.ts";
import ReviewSubmissionPopup from "./ReviewSubmissionPopup";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

const Testimonials = () => {
  const { language, translate } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { showError } = useToast();
  const isRTL = language === "ar";
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const visibleCount = 3;

  // Fetch reviews from API (only approved reviews are returned)
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await api.get("/reviews");

        // Filter to ensure only approved reviews are shown (API should already do this, but double-check)
        let reviewsData = [];

        // Handle different response structures
        if (response.data) {
          // If response.data.data is an array
          if (response.data.data && Array.isArray(response.data.data)) {
            reviewsData = response.data.data.filter(
              (review) =>
                review.status === "approved" || review.status === "Approved"
            );
          }
        }

        setReviews(reviewsData);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Reset slider when language or data changes
  useEffect(() => {
    setStartIndex(0);
  }, [language, reviews]);

  const total = reviews.length;

  const next = () => {
    if (!total) return;
    setStartIndex((prev) => (prev + 1) % total);
  };

  const prev = () => {
    if (!total) return;
    setStartIndex((prev) => (prev - 1 + total) % total);
  };

  // Clamp start index so we don't wrap around and can disable buttons at ends
  const clampedStart = total
    ? Math.min(startIndex, Math.max(0, total - visibleCount))
    : 0;
  const pageItems = total
    ? reviews.slice(clampedStart, Math.min(clampedStart + visibleCount, total))
    : [];

  const isPrevDisabled = total === 0 || clampedStart === 0;
  const isNextDisabled = total === 0 || clampedStart + visibleCount >= total;

  const handleReviewClick = () => {
    if (!isAuthenticated) {
      showError(
        translate("testimonials.messages.loginRequired") ||
          "Please login to submit a review"
      );
      return;
    }
    setShowPopup(true);
  };

  const handlePopupSuccess = () => {
    // Optionally refresh reviews after successful submission
    // You might want to refetch after a delay to allow admin approval
  };

  if (loading) {
    return (
      <section className='py-12 px-4'>
        <div className='max-w-7xl mx-auto text-center py-12'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#e95923] mx-auto'></div>
          <p className='mt-4 text-gray-600'>Loading testimonials...</p>
        </div>
      </section>
    );
  }

  return (
    <section className='py-12 px-4'>
      <div
        className='relative max-w-7xl mx-auto rounded-2xl overflow-hidden'
        style={{
          backgroundImage: `url(${ourclientsayimage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Overlay */}
        <div className='absolute inset-0 bg-black bg-opacity-60' />

        <div className='relative z-10 p-6 sm:p-8 md:p-12'>
          <h2
            className={`text-white text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center ${
              language === "ar" ? "sm:text-right" : "sm:text-left"
            }`}
          >
            {translations[language].testimonials.titleFirst}{" "}
            <span className='text-[#e95923]'>
              {translations[language].testimonials.titleHighlight}
            </span>{" "}
            {translations[language].testimonials.titleLast}
          </h2>

          <div
            className='grid gap-8 lg:gap-8 
             grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-3'
          >
            {pageItems.length === 0 ? (
              <div className='col-span-full text-center py-8'>
                <p className='text-white text-lg'>
                  No testimonials available yet.
                </p>
              </div>
            ) : (
              pageItems.map((review, i) => {
                const rating = Math.max(
                  0,
                  Math.min(5, parseFloat(review.rating) || 0)
                );
                return (
                  <div
                    key={`${clampedStart}-${i}-${
                      review.id || review.name || "item"
                    }`}
                    className='bg-white rounded-xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-shadow'
                  >
                    <h3 className='font-bold text-lg'>
                      {review.name || "Anonymous"}
                    </h3>

                    {review.designation && (
                      <p className='text-gray-700 mb-4'>
                        "{review.designation}"
                      </p>
                    )}

                    <p
                      dangerouslySetInnerHTML={{
                        __html: review.testimonial || "",
                      }}
                      className='text-gray-500 text-sm mb-4'
                    />

                    <div className='flex items-center justify-between mt-2'>
                      <div
                        className='flex items-center gap-2'
                        aria-label={`Rated ${rating.toFixed(1)} out of 5`}
                      >
                        <div className='relative inline-block leading-none text-[18px]'>
                          <div className='text-gray-300 tracking-tight select-none'>
                            ★★★★★
                          </div>
                          <div
                            className='absolute inset-0 overflow-hidden'
                            style={{ width: `${(rating / 5) * 100}%` }}
                          >
                            <div className='text-[#e95923] tracking-tight select-none'>
                              ★★★★★
                            </div>
                          </div>
                        </div>
                        <span className='text-gray-600 text-sm'>
                          {rating.toFixed(1)}
                        </span>
                      </div>

                      {review.createdAt && (
                        <span className='text-gray-400 text-xs'>
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div
            className={`absolute top-4 sm:top-6 flex gap-3 z-20 ${
              isRTL ? "left-4 sm:left-12" : "right-4 sm:right-12"
            }`}
          >
            <button
              className={`bg-[#000000] p-2 rounded-md shadow hover:bg-darkGraybg ${
                isPrevDisabled
                  ? "opacity-50 cursor-not-allowed hover:bg-[#000000]"
                  : ""
              }`}
              onClick={isRTL ? next : prev}
              aria-label={translations[language].testimonials.prevAria}
              type='button'
              disabled={isPrevDisabled}
            >
              {isRTL ? (
                <ChevronRight className='text-white' />
              ) : (
                <ChevronLeft className='text-white' />
              )}
            </button>
            <button
              className={`bg-[#000000] p-2 rounded-md shadow hover:bg-darkGraybg ${
                isNextDisabled
                  ? "opacity-50 cursor-not-allowed hover:bg-[#000000]"
                  : ""
              }`}
              onClick={isRTL ? prev : next}
              aria-label={translations[language].testimonials.nextAria}
              type='button'
              disabled={isNextDisabled}
            >
              {isRTL ? (
                <ChevronLeft className='text-[#E95923]' />
              ) : (
                <ChevronRight className='text-[#E95923]' />
              )}
            </button>
          </div>
        </div>

        <div className='relative z-20 flex justify-center sm:justify-end mt-6 px-6 sm:px-8 md:px-12'>
          <button
            className='bg-[#e95923] hover:bg-orange-600 text-white px-6 py-2 mb-2 rounded'
            type='button'
            onClick={handleReviewClick}
          >
            {translations[language].testimonials.reviewUs}
          </button>
        </div>
      </div>

      {/* Review Submission Popup */}
      <ReviewSubmissionPopup
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        onSuccess={handlePopupSuccess}
      />
    </section>
  );
};

export default Testimonials;
